/**
 * tarefasRepository
 * Centraliza a leitura de tarefas por escopo:
 * * Obra (TarefasList)
 * * Responsável (MinhasTarefas)
 *
 * Implementação inicial: delega aos services existentes,
 * para não quebrar a UI. Futuras mudanças (ex.: VIEW v_tarefas_ext)
 * serão feitas aqui sem tocar nas páginas.
 * 
 * ✅ NOVA FUNCIONALIDADE: Normalização automática de datas
 * - Datas sem horário (YYYY-MM-DD) são automaticamente ancoradas no meio-dia local (12:00:00)
 * - Evita problemas de timezone que causavam "salvei num dia e apareceu no dia anterior"
 * - Aplicada em todos os campos de data: data_inicio, data_limite, data_conclusao, data_limite_recorrencia
 */
import * as notificationsService from '@/features/comunicacao/services/notificationsService';
import { supabase } from '@/features/sistema/services/supabaseClient';
// lazy memo: cacheService
let __cacheSvc: any;
async function getCacheService() {
  if (!__cacheSvc) {
    const mod = await import('@/features/sistema/services/cacheService');
    __cacheSvc = mod.cacheService;
    try { await __cacheSvc.init?.(); } catch {}
  }
  return __cacheSvc;
}
// notificationsService - import estático (já está no bundle principal)
function getNotificationsService() {
  return notificationsService;
}
// lazy memo: mentionsService
let __mentionsMod: any;
async function getMentions() {
  if (!__mentionsMod) {
    __mentionsMod = await import('@/features/comunicacao/services/mentionsService');
  }
  return __mentionsMod;
}
import type { Usuario } from '@/features/comunicacao/services/mentionsService';
import { offlineSyncService } from '@/features/sistema/services/offilineSyncService';

import { isUUID,Tarefa } from '../utils/tarefasUtils';

/** Tipos base usados na UI */
export type TarefaCompleta = {
  id: string;
  nome: string;
  descricao: string | null;
  status: 'nao_iniciado' | 'orcado' | 'aguardando' | 'agendar' | 'agendado' | 'em_andamento' | 'parado' | 'concluido';
  prioridade: 'baixa' | 'media' | 'alta' | 'critica';
  obra_id: string;
  grupo_id: string | null;
  responsavel_id: string | null;
  responsaveis_ids?: string[];
  ativo: boolean;
  ordem: number;
  etapa: string | null;
  subetapa_id: string | null;
  data_inicio: string | null;   // ISO
  data_inicio_tem_hora?: boolean | null;
  data_limite: string | null;   // ISO
  data_conclusao: string | null;// ISO
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;
  // recorrência etc...
  recorrente?: boolean;
  padrao_recorrencia?: string | null;
  intervalo_recorrencia?: number | null;
  dias_semana?: string[] | null;
  data_limite_recorrencia?: string | null;
  tarefa_pai_id?: string | null;
  proxima_ocorrencia?: string | null;
  recorrencia_ativa?: boolean;
  progresso_pct?: number;
};

export type ObraSlim = { id: string; nome: string; is_personal?: boolean };
export type GrupoTarefa = { 
  id: string; 
  nome: string; 
  cor: string; 
  ordem: number; 
  obra_id: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
};

export type TarefaComObra = TarefaCompleta & {
  obra?: ObraSlim;
  grupos_tarefas?: { 
    id: string; 
    nome: string; 
    cor: string; 
    ordem: number; 
    obra_id: string;
    created_at: string;
    updated_at: string;
  } | null;
  created_by?: string | null;
  updated_by?: string | null;
  _isVirtualTask?: boolean;
};

function mapResponsaveis(t: any): TarefaComObra {
  const responsaveis_ids =
    Array.isArray(t.tarefas_responsaveis) ? t.tarefas_responsaveis.map((r: any) => r.usuario_id) : [];
  return {
    ...t,
    responsaveis_ids,
    obra: t.obra ?? t.obras ?? undefined,
    grupos_tarefas: t.grupos_tarefas ?? null,
  };
}

/** Lista tarefas de uma obra (ativas) */
export async function listTarefasByObra(obraId: string): Promise<TarefaComObra[]> {
  const { data, error } = await supabase
    .from("tarefas")
    .select(`
      *,
      obras:obras!tarefas_obra_id_fkey ( id, nome, is_personal ),
      tarefas_responsaveis ( usuario_id ),
      grupos_tarefas:grupos_tarefas!tarefas_grupo_id_fkey ( id, nome, obra_id )
    `)
    .eq("obra_id", obraId)
    .eq("ativo", true)
    .order("ordem", { ascending: true });

  if (error) throw error;
  return (data || []).map(mapResponsaveis);
}

/** Lista "minhas tarefas" para múltiplos responsáveis (abertas+concluídas; a UI filtra) */
export async function listMinhasTarefas(responsavelIds: string[]): Promise<TarefaComObra[]> {
  if (!responsavelIds || responsavelIds.length === 0) return [];

  const selectShape = `
    *,
    obras:obras!tarefas_obra_id_fkey ( id, nome, is_personal ),
    tarefas_responsaveis!left ( usuario_id ),
    grupos_tarefas:grupos_tarefas!tarefas_grupo_id_fkey ( id, nome, obra_id )
  `;

  // Buscar em paralelo: responsável direto + IDs via tabela de junção
  // NOTA: Não filtrar diretamente em "tarefas_responsaveis.usuario_id" pois o PostgREST
  // filtra também os dados embutidos, retornando apenas o usuário que deu match
  // em vez de TODOS os responsáveis da tarefa.
  const [r1, rJunction] = await Promise.all([
    // Responsável direto (campo na tabela tarefas)
    supabase
      .from("tarefas")
      .select(selectShape)
      .eq("ativo", true)
      .in("responsavel_id", responsavelIds)
      .order("created_at", { ascending: false })
      .range(0, 4999),
    // IDs de tarefas via tabela de junção (query leve, sem embed)
    supabase
      .from("tarefas_responsaveis")
      .select("tarefa_id")
      .in("usuario_id", responsavelIds)
  ]);

  if (r1.error) throw r1.error;
  if (rJunction.error) throw rJunction.error;

  const d1 = (r1.data || []).map(mapResponsaveis);
  const idsJaCarregados = new Set(d1.map(t => t.id));

  // IDs que só existem via junção (não encontrados por q1)
  const idsRestantes = [...new Set(
    (rJunction.data || []).map((j: any) => j.tarefa_id)
  )].filter(id => !idsJaCarregados.has(id));

  // Buscar tarefas restantes por ID — embed retorna TODOS os responsáveis
  let d2: TarefaComObra[] = [];
  if (idsRestantes.length > 0) {
    const { data, error } = await supabase
      .from("tarefas")
      .select(selectShape)
      .eq("ativo", true)
      .in("id", idsRestantes)
      .order("created_at", { ascending: false })
      .range(0, 4999);
    if (error) throw error;
    d2 = (data || []).map(mapResponsaveis);
  }

  return [...d1, ...d2];
}

/** Grupos de uma obra */
export async function getGruposTarefasByObra(obraId: string): Promise<GrupoTarefa[]> {
  const { data, error } = await supabase
    .from("grupos_tarefas")
    .select("id, nome, cor, ordem, obra_id, created_at, updated_at, created_by, updated_by")
    .eq("obra_id", obraId)
    .order("ordem", { ascending: true });
  if (error) throw error;
  return data || [];
}

/** Usuários (todos ativos, excluindo clientes) — usado em filtros/mentions */
export async function getUsuariosAtivos(): Promise<{id: string; nome: string}[]> {
  const { data, error } = await supabase
    .from("usuarios")
    .select("id, nome, role, funcao")
    .eq("status", "ativo")
    .neq("role", "cliente")
    .neq("funcao", "cliente")
    .order("nome");
  if (error) throw error;
  return data || [];
}

/** Usuário por id (slim) */
export async function getUsuarioById(userId: string): Promise<{id: string; nome: string} | null> {
  const { data, error } = await supabase
    .from("usuarios")
    .select("id, nome")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

/** Contagem de comentários por tarefa (retorna { [tarefaId]: count }) */
/**
 * Conta comentários ativos por tarefa
 * @param tarefaIds - IDs das tarefas para filtrar (otimização). Se não fornecido, conta todos.
 * @returns Mapa { tarefaId: count }
 */
export async function contarComentarios(tarefaIds?: string[]): Promise<Record<string, number>> {
  const counts: Record<string, number> = {};

  async function fetchCounts(ids?: string[]) {
    let query = supabase
      .from('app_comments')
      .select(
        `
          id,
          thread:app_comment_threads!inner(
            recurso,
            recurso_id
          )
        `,
      )
      .eq('thread.recurso', 'tarefa')
      .is('deleted_at', null);

    if (ids && ids.length > 0) {
      query = query.in('thread.recurso_id', ids);
    }

    const { data, error } = await query;
    if (error) throw error;

    (data ?? []).forEach((row: any) => {
      const tarefaId = row.thread?.recurso_id;
      if (!tarefaId) return;
      counts[tarefaId] = (counts[tarefaId] ?? 0) + 1;
    });
  }

  if (!tarefaIds || tarefaIds.length === 0) {
    console.log('📊 contarComentarios: Buscando todos os comentários em app_comments');
    await fetchCounts();
    return counts;
  }

  console.log('📊 contarComentarios: Buscando comentários para', tarefaIds.length, 'tarefas (chunks app_comments)');
  const chunkSize = 200;
  for (let i = 0; i < tarefaIds.length; i += chunkSize) {
    await fetchCounts(tarefaIds.slice(i, i + chunkSize));
  }

  console.log('✅ contarComentarios: Contagens processadas:', Object.keys(counts).length, 'tarefas com comentários');
  return counts;
}

type TaskWrite = {
  id?: string
  obra_id?: string
  nome?: string
  descricao?: string | null
  dificuldade?: number | null
  status?: Tarefa['status']
  prioridade?: string
  data_inicio?: string | null
  data_limite?: string | null
  grupo_id?: string | null
  subetapa_id?: string | null
  responsavel_id?: string | null
  responsaveis_ids?: string[]
  progresso_pct?: number
}

// Util: validação de UUID v4 (suficiente para sanitização)
// Agora importada de tarefasUtils.ts

// Remove grupo_id inválido e campos de UI de um payload de escrita
function stripInvalidGrupoId<T extends { grupo_id?: unknown }>(obj: T): T {
  const clone = { ...(obj as any) };
  
  // Nunca enviar campos apenas de UI
  delete clone.grupo_virtual;
  delete clone.obra;
  delete clone._obra;
  delete clone._ui;
  
  // Se grupo_id não for UUID válido (ex.: 'atividades-semana'), remova
  if ('grupo_id' in clone && (typeof clone.grupo_id !== 'string' || !isUUID(clone.grupo_id))) {
    delete clone.grupo_id;
  }
  
  return clone;
}

// ✅ NOVA FUNÇÃO: Normalizar entrada de datas para evitar problemas de timezone
function normalizeDateInput(value: string | null): string | null {
  if (!value) return null;
  // se veio apenas YYYY-MM-DD, ancorar 12:00 local antes de mandar pro DB
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T12:00:00`;
  return value;
}

// ✅ FUNÇÃO AUXILIAR: Normalizar todos os campos de data de uma vez
function normalizeAllDateFields(obj: any): any {
  const camposData = ['data_inicio', 'data_limite', 'data_conclusao', 'data_limite_recorrencia'];
  const result = { ...obj };
  
  // Derivar data_inicio_tem_hora quando não vier explícito no patch
  if ('data_inicio' in result) {
    if (result.data_inicio == null) {
      // zera o flag também quando limpar a data
      if ('data_inicio_tem_hora' in result) result.data_inicio_tem_hora = null;
    } else if (typeof result.data_inicio === 'string' && !('data_inicio_tem_hora' in result)) {
      // Se o flag não vier, inferir com base no horário LOCAL:
      // 12:00 (meio-dia) é o sentinela para "sem hora".
      try {
        const d = new Date(result.data_inicio);
        if (!isNaN(d.getTime())) {
          const hasClock = !(d.getHours() === 12 && d.getMinutes() === 0 && d.getSeconds() === 0);
          result.data_inicio_tem_hora = hasClock;
        } else {
          // fallback ultra conservador
          result.data_inicio_tem_hora = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(result.data_inicio);
        }
      } catch {
        result.data_inicio_tem_hora = false;
      }
    }
  }

  for (const campo of camposData) {
    if (result[campo]) {
      result[campo] = normalizeDateInput(result[campo]);
    }
  }
  
  return result;
}

// ✅ SANITIZAR: Função para limpar dados de inserção (removida duplicata)

// Tipo para inserção (comentado - não utilizado atualmente)
// type InsertTarefa = {
//   nome: string;
//   descricao?: string | null;
//   status: Tarefa['status'];
//   prioridade: Tarefa['prioridade'];
//   data_inicio?: string | null;
//   data_limite?: string | null;
//   obra_id: string;
//   grupo_id?: string | null;
//   responsavel_id?: string | null;
//   responsaveis_ids?: string[];
//   ordem?: number;
//   ativo?: boolean;
//   progresso_pct?: number;
//   recorrente?: boolean;
//   padrao_recorrencia?: Tarefa['padrao_recorrencia'];
//   intervalo_recorrencia?: number | null;
//   dias_semana?: Tarefa['dias_semana'];
//   data_limite_recorrencia?: string | null;
//   tarefa_pai_id?: string | null;
//   proxima_ocorrencia?: string | null;
//   recorrencia_ativa?: boolean;
//   created_by?: string | null;
//   updated_by?: string | null;
// };

// ✅ util: decide grupo automaticamente a partir de datas/status
async function coerceGrupoId(
  obraId: string,
  maybeGrupoId: string | null | undefined,
  data_inicio?: string | null,
  data_limite?: string | null,
  status?: string | null
): Promise<string | null> {
  if (maybeGrupoId) return maybeGrupoId; // já veio um UUID válido
  // regra simplificada (igual ao service): atrasadas/semana/futuras/concluídas
  // => busque ids dos grupos padrão da obra e mapeie por "nome"
  const grupos = await getGruposPadraoByObra(obraId);
  return pickGrupoIdByDates(grupos, { data_inicio, data_limite, status }); // mesma heurística do service
}

// Função auxiliar para buscar grupos padrão da obra
async function getGruposPadraoByObra(obraId: string): Promise<any[]> {
  const { data, error } = await supabase
    .from('grupos_tarefas')
    .select('id, nome, cor, ordem, obra_id, created_at, updated_at')
    .eq('obra_id', obraId);
  if (error) throw error;
  return data ?? [];
}

// Função auxiliar para escolher grupo baseado em datas/status
function pickGrupoIdByDates(grupos: any[], ctx: { data_inicio?: string | null; data_limite?: string | null; status?: string | null }): string | null {
  if (!grupos.length) return null;
  
  // 1) Concluídas tem precedência
  if (ctx.status && ctx.status.toLowerCase().includes('conclu')) {
    const concluidas = grupos.find(g => g.nome.toLowerCase().includes('conclu') || g.nome.toLowerCase().includes('finaliz'));
    if (concluidas) return concluidas.id;
  }
  
  // 2) Por data
  const hoje = new Date();
  const ref = ctx.data_inicio ? new Date(ctx.data_inicio) : ctx.data_limite ? new Date(ctx.data_limite) : hoje;
  
  // Semana atual: domingo 00:00 -> sábado 23:59:59.999
  const dt = new Date(hoje);
  dt.setHours(0, 0, 0, 0);
  const day = dt.getDay(); // 0=Dom
  const start = new Date(dt);
  start.setDate(dt.getDate() - day);
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  end.setMilliseconds(end.getMilliseconds() - 1);
  
  if (ref >= start && ref <= end) {
    const semana = grupos.find(g => g.nome.toLowerCase().includes('semana'));
    if (semana) return semana.id;
  }
  
  if (ref > end) {
    const futuras = grupos.find(g => g.nome.toLowerCase().includes('futur'));
    if (futuras) return futuras.id;
  }
  
  // Sem data / atrasadas -> semana por padrão
  const semana = grupos.find(g => g.nome.toLowerCase().includes('semana'));
  return semana ? semana.id : grupos[0]?.id || null;
}

// Função auxiliar para garantir grupos padrão
async function ensureDefaultGrupos(obraId: string): Promise<void> {
  const grupos = await getGruposPadraoByObra(obraId);
  if (grupos.length === 0) {
    // Se não há grupos, criar os padrão
    const gruposPadrao = [
      { nome: 'Atividades da Semana', cor: '#3B82F6', ordem: 1 },
      { nome: 'Atividades Futuras', cor: '#8B5CF6', ordem: 2 },
      { nome: 'Concluídas', cor: '#10B981', ordem: 3 }
    ];
    
    const timestamp = new Date().toISOString();
    const gruposParaCriar = gruposPadrao.map(grupo => ({
      ...grupo,
      obra_id: obraId,
      created_at: timestamp,
      updated_at: timestamp
    }));
    
    const { error } = await supabase
      .from('grupos_tarefas')
      .insert(gruposParaCriar);
    
    if (error) throw error;
  }
}

export async function createTask(input: {
  obra_id: string;
  nome: string;
  descricao?: string | null;
  status?: string;
  data_inicio?: string | null;
  data_inicio_tem_hora?: boolean; // ✅ ADICIONADO: Flag para indicar se data_inicio tem horário
  data_limite?: string | null;
  grupo_id?: string | null; // ✅ agora opcional
  responsaveis_ids?: string[];
  multiplos_responsaveis?: boolean; // ✅ ADICIONADO: Campo para controlar validação de responsavel_id
  responsavel_id?: string | null; // ✅ ADICIONADO: Campo fallback para responsável único
  prioridade?: string; // ✅ ADICIONADO: Campo prioridade
}): Promise<Tarefa> {
  // ✅ garantir grupos padrão e decidir grupo automaticamente
  await ensureDefaultGrupos(input.obra_id);
  const grupo_id = await coerceGrupoId(
    input.obra_id,
    input.grupo_id,
    input.data_inicio ?? null,
    input.data_limite ?? null,
    input.status ?? null
  );
  const { responsaveis_ids = [], ...rest } = input;

  // ✅ SANITIZAR: Usar função sanitizeInsertTask para limpar dados
  const dadosSanitizados = sanitizeInsertTask({ ...rest, grupo_id });

  // ✅ NORMALIZAR DATAS: Aplicar normalização para evitar problemas de timezone
  const dadosComDatasNormalizadas = normalizeAllDateFields(dadosSanitizados);

  const { data, error } = await supabase
    .from('tarefas')
    .insert(dadosComDatasNormalizadas)
    .select(`
      *,
      grupos_tarefas (id, nome, cor, ordem, obra_id, created_at, updated_at),
      created_by_user:usuarios!tarefas_created_by_fkey (id, nome, email),
      updated_by_user:usuarios!tarefas_updated_by_fkey (id, nome, email)
    `) // ✅ sem slug
    .single();
  if (error) throw error;

  if (responsaveis_ids.length) {
    const rows = responsaveis_ids.map((uid) => ({ tarefa_id: data.id, usuario_id: uid }));
    const { error: linkErr } = await supabase.from('tarefas_responsaveis').insert(rows);
    if (linkErr) throw linkErr;
  }
  return data as Tarefa;
}

export async function updateTask(
  tarefaId: string,
  patch: TaskWrite
): Promise<any> {
  // 🧹 evita 22P02 quando alguém manda slug no grupo_id
  const patchSan = stripInvalidGrupoId(patch)
  const { responsaveis_ids, ...patchSemResponsaveis } = patchSan
  let previousStatus: string | null | undefined = undefined
  const shouldTrackStatusChange = typeof patchSemResponsaveis.status === 'string'

  if (shouldTrackStatusChange) {
    const { data: currentRow, error: currentStatusError } = await supabase
      .from('tarefas')
      .select('status')
      .eq('id', tarefaId)
      .single()

    if (currentStatusError) {
      console.error('? updateTask: erro ao buscar status atual antes do update', currentStatusError)
    } else {
      previousStatus = (currentRow as { status?: string | null })?.status ?? null
    }
  }
  
  // ✅ NORMALIZAR DATAS: Aplicar normalização para evitar problemas de timezone
  const patchComDatasNormalizadas = normalizeAllDateFields(patchSemResponsaveis);
  
  console.log(`🔄 updateTask: Atualizando tarefa ${tarefaId} com payload:`, patchComDatasNormalizadas);
  
  const { data, error } = await supabase
    .from('tarefas')
    .update(patchComDatasNormalizadas)
    .eq('id', tarefaId)
    .select('*')
    .single();
    
  if (error) throw error;
  
  // Sincronizar responsáveis se necessário
  if (responsaveis_ids !== undefined) {
    console.log(`🔄 updateTask: Sincronizando responsáveis para tarefa ${tarefaId}:`, responsaveis_ids);
    await setTaskResponsaveis(tarefaId, responsaveis_ids);
  }
  if (shouldTrackStatusChange && previousStatus !== undefined) {
    const nextStatus = (data as { status?: string | null })?.status ?? null
    await maybeCreateStatusChangeComment({
      tarefaId,
      previousStatus,
      nextStatus,
    })
  }
  
  
  console.log(`✅ updateTask: Tarefa ${tarefaId} atualizada com sucesso:`, data);
  return data;
}

async function maybeCreateStatusChangeComment(params: {
  tarefaId: string
  previousStatus: string | null
  nextStatus: string | null
}) {
  const { tarefaId, previousStatus, nextStatus } = params
  if (!tarefaId) return
  if ((previousStatus ?? null) === (nextStatus ?? null)) return

  try {
    await supabase.rpc('fn_tarefa_status_comment', {
      p_tarefa_id: tarefaId,
      p_status_anterior: previousStatus,
      p_status_novo: nextStatus,
    })
  } catch (rpcError) {
    console.error('? updateTask: erro ao registrar coment�rio autom�tico de status', rpcError)
  }
}

// Função auxiliar para buscar tarefa por ID (comentada - não utilizada atualmente)
// async function getById(id: string): Promise<Tarefa> {
//   const { data, error } = await supabase
//     .from('tarefas')
//     .select('*')
//     .eq('id', id)
//     .single();
//   if (error) throw error;
//   return data as Tarefa;
// }

export async function deleteTask(id: string): Promise<void> {
  const { error } = await supabase
    .from('tarefas')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function setTaskResponsaveis(
  tarefaId: string,
  responsaveisIds: string[]
): Promise<void> {
  console.log(`🔄 setTaskResponsaveis: Iniciando sincronização para tarefa ${tarefaId}`);
  console.log(`🔄 setTaskResponsaveis: Responsáveis recebidos:`, responsaveisIds);

  // 1️⃣ Buscar responsáveis atuais
  const { data: atuais, error: fetchError } = await supabase
    .from('tarefas_responsaveis')
    .select('usuario_id')
    .eq('tarefa_id', tarefaId);

  if (fetchError) throw fetchError;

  const idsAtuais = new Set((atuais || []).map(r => r.usuario_id));
  const idsNovos = new Set(responsaveisIds);

  console.log(`🔍 setTaskResponsaveis: Responsáveis atuais:`, Array.from(idsAtuais));
  console.log(`🔍 setTaskResponsaveis: Responsáveis novos:`, Array.from(idsNovos));

  // 2️⃣ Calcular diff (quem adicionar e quem remover)
  const adicionar = responsaveisIds.filter(id => !idsAtuais.has(id));
  const remover = Array.from(idsAtuais).filter(id => !idsNovos.has(id));

  console.log(`➕ setTaskResponsaveis: Adicionar:`, adicionar);
  console.log(`➖ setTaskResponsaveis: Remover:`, remover);

  // 3️⃣ Se nada mudou, não fazer nada (evita spam de triggers)
  if (adicionar.length === 0 && remover.length === 0) {
    console.log('✅ setTaskResponsaveis: Nenhuma mudança detectada - nada a fazer');
    return;
  }

  // 4️⃣ Remover apenas os que saíram
  if (remover.length > 0) {
    console.log(`🗑️ setTaskResponsaveis: Removendo ${remover.length} responsável(is)...`);
    const { error: deleteError } = await supabase
      .from('tarefas_responsaveis')
      .delete()
      .eq('tarefa_id', tarefaId)
      .in('usuario_id', remover);

    if (deleteError) throw deleteError;
    console.log(`✅ setTaskResponsaveis: Responsáveis removidos com sucesso`);
  }

  // 5️⃣ Adicionar apenas os novos (isso deve disparar trigger/comentário de sistema)
  if (adicionar.length > 0) {
    console.log(`➕ setTaskResponsaveis: Adicionando ${adicionar.length} responsável(is)...`);
    const novasLinhas = adicionar.map(id => ({
      tarefa_id: tarefaId,
      usuario_id: id
    }));

    const { error: insertError } = await supabase
      .from('tarefas_responsaveis')
      .insert(novasLinhas);

    if (insertError) throw insertError;
    console.log(`✅ setTaskResponsaveis: Responsáveis adicionados com sucesso`);
  }

  console.log(`✅ setTaskResponsaveis: Sincronização concluída (+${adicionar.length} -${remover.length})`);
}

// ========================================
// FUNÇÕES PARA COMENTÁRIOS DE TAREFAS
// ========================================

export async function createTarefaComentario(comentarioData: any, usuarios: any[] = []): Promise<any> {
  try {
    console.log(`💬 createTarefaComentario: Criando comentário para tarefa ${comentarioData.tarefa_id}`);

    // Processar menções se houver usuários
    let mencoes: string[] = [];
    if (usuarios.length > 0 && comentarioData.conteudo) {
      const { processarMencoes } = await import('@features/comunicacao/services/mentionsService');
      const mencoesProcessadas = processarMencoes(comentarioData.conteudo, usuarios);
      mencoes = mencoesProcessadas.usuariosIds;
    }

    const { data, error } = await supabase
      .from('tarefas_comentarios')
      .insert({
        ...comentarioData,
        mencoes: mencoes.length > 0 ? mencoes : null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select(`
        *,
        criado_por:usuarios!tarefas_comentarios_created_by_fkey (id, nome, email),
        atualizado_por:usuarios!tarefas_comentarios_updated_by_fkey (id, nome, email)
      `)
      .single();

    if (error) throw error;

    // 🔔 Criar notificações de menção (se houver)
    if (mencoes.length > 0 && data?.id) {
      try {
        const { criarNotificacoesMencao } = notificationsService;

        // Buscar dados da tarefa e autor para compor a notificação
        const [tarefa, autor] = await Promise.all([
          getTaskById(data.tarefa_id),          // precisa de nome e obra_id
          getUsuarioById(data.created_by)         // para autorNome
        ]);

        await criarNotificacoesMencao({
          usuariosIds: mencoes.filter((uid: string) => uid !== data.created_by),
          comentario: {
            id: data.id,
            conteudo: data.conteudo,
            tarefa_id: data.tarefa_id,
            created_by: data.created_by
          },
          tarefa: {
            id: tarefa.id,
            nome: tarefa.nome,
            obra_id: tarefa.obra_id
          },
          autorNome: autor?.nome || 'Alguém'
        });
      } catch (e) {
        console.warn('⚠️ Erro ao criar notificações de menção:', e);
      }
    }

    console.log(`✅ createTarefaComentario: Comentário criado com sucesso`);
    return data;

  } catch (error) {
    console.error('❌ Erro em createTarefaComentario:', error);
    throw error;
  }
}

export async function getTarefaComentariosComMencoes(tarefaId: string) {
  try {
    console.log('🔍 getTarefaComentariosComMencoes: Buscando comentários para tarefa:', tarefaId);
    
    // 1) Busca comentários com JOIN para usuários
    const { data, error } = await supabase
      .from('tarefas_comentarios')
      .select(`
        *,
        criado_por:usuarios!tarefas_comentarios_created_by_fkey (id, nome, email),
        atualizado_por:usuarios!tarefas_comentarios_updated_by_fkey (id, nome, email)
      `)
      .eq('tarefa_id', tarefaId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    
    // ✅ DEBUG: Verificar dados carregados
    console.log('🔍 getTarefaComentariosComMencoes: Comentários carregados:', (data || []).length);
    if (data && data.length > 0) {
      console.log('🔍 getTarefaComentariosComMencoes: Primeiro comentário:', {
        id: data[0].id,
        criado_por: data[0].criado_por,
        created_by: data[0].created_by,
        conteudo: data[0].conteudo?.substring(0, 50) + '...'
      });
    }

    if (!data || data.length === 0) return [];

    // 2) Stats das curtidas via view
    const ids = data.map(c => c.id);
    const { data: stats, error: statsErr } = await supabase
      .from('v_comentarios_stats')
      .select('id, curtidas_count, curtido_por_mim')
      .in('id', ids);
    
    if (statsErr) {
      console.warn('[comentarios] falha ao carregar stats, retornando sem stats', statsErr);
      return data;
    }
    
    const mapStats = new Map(stats?.map(s => [s.id, s]) || []);

    // 3) Merge e retorno
    const comStats = data.map(c => {
      const s = mapStats.get(c.id) as any;
      return { 
        ...c, 
        curtidas_count: s?.curtidas_count ?? 0, 
        curtido_por_mim: !!s?.curtido_por_mim 
      };
    });

    // ✅ NOVA ORDENAÇÃO: Fixados primeiro (por ordem_fixacao), depois mais recentes
    const comentariosOrdenados = comStats.sort((a, b) => {
      // 1º: Comentários fixados vêm primeiro (maior ordem_fixacao = mais prioritário)
      if (a.fixado && !b.fixado) return -1;
      if (!a.fixado && b.fixado) return 1;
      
      // 2º: Entre fixados, ordenar por ordem_fixacao (decrescente)
      if (a.fixado && b.fixado) {
        const ordemA = a.ordem_fixacao || 0;
        const ordemB = b.ordem_fixacao || 0;
        return ordemB - ordemA; // Decrescente (maior primeiro)
      }
      
      // 3º: Comentários não fixados: mais recentes primeiro (decrescente por created_at)
      const dataA = new Date(a.created_at).getTime();
      const dataB = new Date(b.created_at).getTime();
      return dataB - dataA; // Decrescente (mais recente primeiro)
    });

    return comentariosOrdenados;
  } catch (e) {
    console.error('❌ Erro em getTarefaComentariosComMencoes:', e);
    throw e;
  }
}

// ==========================================================
// ✅ SUPORTE A THREADS (genérico para Checklist / Cronograma)
// ==========================================================

/** Garante/retorna a thread para um recurso genérico (ex.: 'checklist_item') */
export async function ensureThreadGeneric(
  recurso: 'tarefa' | 'checklist_item' | 'cronograma_etapa' | 'cronograma_subetapa',
  recursoId: string,
  obraId: string,
  userId?: string | null,
) {
  // 1) tenta achar
  const { data: found, error: findErr } = await supabase
    .from('comment_threads')
    .select('id')
    .eq('recurso', recurso)
    .eq('recurso_id', recursoId)
    .maybeSingle();
  if (findErr) throw findErr;
  if (found?.id) return found.id as string;

  // 2) cria com tratamento de race condition
  const { data: created, error: insErr } = await supabase
    .from('comment_threads')
    .insert({ recurso, recurso_id: recursoId, obra_id: obraId, created_by: userId ?? null })
    .select('id')
    .single();
  
  if (insErr) {
    // ✅ TRATAMENTO DE RACE CONDITION: Se erro for de constraint única, tentar buscar novamente
    if (insErr.code === '23505' && insErr.message?.includes('comment_threads_unique')) {
      const { data: retryFound, error: retryErr } = await supabase
        .from('comment_threads')
        .select('id')
        .eq('recurso', recurso)
        .eq('recurso_id', recursoId)
        .maybeSingle();
      
      if (retryErr) throw retryErr;
      if (retryFound?.id) return retryFound.id as string;
    }
    throw insErr;
  }
  
  return created!.id as string;
}

/** Lista comentários por thread (ordem cronológica, sem deletados) */
export async function getComentariosByThread(threadId: string) {
  const { data, error } = await supabase
    .from('tarefas_comentarios')
    .select(`
      *,
      criado_por:usuarios!tarefas_comentarios_created_by_fkey (id, nome, email),
      atualizado_por:usuarios!tarefas_comentarios_updated_by_fkey (id, nome, email)
    `)
    .eq('thread_id', threadId)
    .is('deleted_at', null)
    .order('fixado', { ascending: false })
    .order('ordem_fixacao', { ascending: true, nullsFirst: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/** Cria comentário em uma thread (processa @menções). Notificações: mantemos só quando for tarefa. */
export async function createComentarioInThread(
  params: { thread_id: string; conteudo: string; tipo?: 'texto'|'foto'|'arquivo'; created_by: string; updated_by?: string },
  usuariosDisponiveis: Array<{ id: string; nome: string; email?: string }> = []
) {
  // menções
  const { processarMencoes } = await getMentions();
  const { texto, usuariosIds } = processarMencoes(params.conteudo || '', usuariosDisponiveis as Usuario[]);
  const payload = {
    ...params,
    conteudo: texto.trim(),
    tipo: params.tipo ?? 'texto',
  };
  const { data, error } = await supabase
    .from('tarefas_comentarios')
    .insert(payload)
    .select('*')
    .single();
  if (error) throw error;

  // 🔔 Notificações de menção: só se for comentário de tarefa (tem tarefa_id)
  if (usuariosIds.length && data?.tarefa_id) {
    try {
      const [tarefa, autor] = await Promise.all([
        getTarefaById(data.tarefa_id),
        getUsuarioById(data.updated_by || data.created_by),
      ]);
      const { criarNotificacoesMencao } = await getNotificationsService();
      await criarNotificacoesMencao({
        usuariosIds: usuariosIds.filter((uid: string) => uid !== (data.updated_by || data.created_by)),
        comentario: { id: data.id, conteudo: data.conteudo, tarefa_id: data.tarefa_id, created_by: data.updated_by || data.created_by },
        tarefa: { id: tarefa?.id, nome: tarefa?.nome, obra_id: tarefa?.obra_id },
        autorNome: autor?.nome || 'Alguém',
      });
    } catch (e) {
      console.warn('⚠️ Falha ao criar notificações de menção (não crítico):', e);
    }
  }
  // cache básico reaproveitando a mesma estrutura
  getCacheService().then(cs => cs.cacheComentarios([data])).catch(() => void 0);
  return data;
}

export async function updateTarefaComentarioCompleto(comentarioId: string, conteudo: string, usuarios: any[] = []): Promise<any> {
  try {
    console.log(`🔄 updateTarefaComentarioCompleto: Atualizando comentário ${comentarioId}`);

    // Processar menções se houver usuários
    let mencoes: string[] = [];
    if (usuarios.length > 0 && conteudo) {
      const { processarMencoes } = await import('@features/comunicacao/services/mentionsService');
      const mencoesProcessadas = processarMencoes(conteudo, usuarios);
      mencoes = mencoesProcessadas.usuariosIds;
    }

    const { data, error } = await supabase
      .from('tarefas_comentarios')
      .update({
        conteudo,
        mencoes: mencoes.length > 0 ? mencoes : null,
        updated_at: new Date().toISOString(),
        editado: true
      })
      .eq('id', comentarioId)
      .select(`
        *,
        criado_por:usuarios!tarefas_comentarios_created_by_fkey (id, nome, email),
        atualizado_por:usuarios!tarefas_comentarios_updated_by_fkey (id, nome, email)
      `)
      .single();

    if (error) throw error;

    // 🔔 (Opcional) Notificar menções também em edição
    if (mencoes.length > 0 && data?.id) {
      try {
        const { criarNotificacoesMencao } = notificationsService;

        const [tarefa, autor] = await Promise.all([
          getTaskById(data.tarefa_id),
          getUsuarioById(data.updated_by || data.created_by)
        ]);
        
        await criarNotificacoesMencao({
          usuariosIds: mencoes.filter((uid: string) => uid !== (data.updated_by || data.created_by)),
          comentario: {
            id: data.id,
            conteudo: data.conteudo,
            tarefa_id: data.tarefa_id,
            created_by: data.updated_by || data.created_by
          },
          tarefa: { id: tarefa.id, nome: tarefa.nome, obra_id: tarefa.obra_id },
          autorNome: autor?.nome || 'Alguém'
        });
      } catch (e) {
        console.warn('⚠️ Erro ao criar notificações de menção (edição):', e);
      }
    }

    console.log(`✅ updateTarefaComentarioCompleto: Comentário atualizado com sucesso`);
    return data;

  } catch (error) {
    console.error('❌ Erro em updateTarefaComentarioCompleto:', error);
    throw error;
  }
}

export async function deleteTarefaComentario(comentarioId: string): Promise<void> {
  try {
    console.log(`🗑️ deleteTarefaComentario: Deletando comentário ${comentarioId}`);

    const { error } = await supabase
      .from('tarefas_comentarios')
      .update({
        deleted_at: new Date().toISOString()
      })
      .eq('id', comentarioId);

    if (error) throw error;
    console.log(`✅ deleteTarefaComentario: Comentário deletado com sucesso`);

  } catch (error) {
    console.error('❌ Erro em deleteTarefaComentario:', error);
    throw error;
  }
}

export async function toggleFixarComentario(comentarioId: string, fixado: boolean, tarefaId: string): Promise<void> {
  try {
    console.log(`📌 toggleFixarComentario: ${fixado ? 'Fixando' : 'Desfixando'} comentário ${comentarioId}`);

    let ordemFixacao = null;
    
    if (fixado) {
      // Buscar maior ordem_fixacao atual
      const { data: comentariosFixados } = await supabase
        .from('tarefas_comentarios')
        .select('ordem_fixacao')
        .eq('tarefa_id', tarefaId)
        .not('ordem_fixacao', 'is', null)
        .order('ordem_fixacao', { ascending: false })
        .limit(1);

      ordemFixacao = comentariosFixados && comentariosFixados.length > 0 
        ? (comentariosFixados[0].ordem_fixacao || 0) + 1 
        : 1;
    }

    const { error } = await supabase
      .from('tarefas_comentarios')
      .update({
        fixado,
        ordem_fixacao: ordemFixacao,
        updated_at: new Date().toISOString()
      })
      .eq('id', comentarioId);

    if (error) throw error;
    console.log(`✅ toggleFixarComentario: Comentário ${fixado ? 'fixado' : 'desfixado'} com sucesso`);

  } catch (error) {
    console.error('❌ Erro em toggleFixarComentario:', error);
    throw error;
  }
}

export function podeEditarComentario(comentario: any, userId?: string, userTipo?: string): boolean {
  if (!userId) return false;
  
  // Admin pode editar qualquer comentário
  if (userTipo === 'admin') return true;
  
  // Usuário pode editar seu próprio comentário
  if (comentario.created_by === userId) return true;
  
  return false;
}

// Função auxiliar para buscar tarefa por ID (usada pelos comentários)
async function getTaskById(id: string): Promise<Tarefa> {
  const { data, error } = await supabase
    .from('tarefas')
    .select('id, nome, obra_id')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data as Tarefa;
}

// ========================================
// ✅ FUNÇÕES TRANSFERIDAS DO tarefasService
// ========================================

// ✅ FUNÇÃO HELPER: Verificar se está online
function isOnline(): boolean {
  return navigator.onLine;
}

// ✅ GRUPOS PADRÃO AUTOMÁTICOS
const GRUPOS_PADRAO = [
  {
    nome: 'Atividades da Semana',
    cor: '#3B82F6',
    ordem: 1,
    tipo: 'semana'
  },
  {
    nome: 'Atividades Futuras',
    cor: '#8B5CF6',
    ordem: 2,
    tipo: 'futuras'
  },
  {
    nome: 'Concluídas',
    cor: '#10B981',
    ordem: 3,
    tipo: 'concluidas'
  }
];

// ✅ NOVA FUNÇÃO: Criar grupos padrão automaticamente
export async function criarGruposPadraoObra(obraId: string, userId?: string): Promise<GrupoTarefa[]> {
  try {
    console.log(`📂 criarGruposPadraoObra: ${isOnline() ? 'Online' : 'Offline'} - obra ${obraId}`);

    if (isOnline()) {
      // Verificar se já existem grupos para esta obra
      const { data: gruposExistentes, error: errorVerificar } = await supabase
        .from('grupos_tarefas')
        .select('*')
        .eq('obra_id', obraId);

      if (errorVerificar) throw errorVerificar;

      // Se já tem grupos, não criar novamente
      if (gruposExistentes && gruposExistentes.length > 0) {
        console.log(`📂 Obra ${obraId} já possui ${gruposExistentes.length} grupos`);
        return gruposExistentes;
      }

      // Criar os 3 grupos padrão
      const timestamp = new Date().toISOString();
      const gruposParaCriar = GRUPOS_PADRAO.map(grupo => ({
        nome: grupo.nome,
        cor: grupo.cor,
        ordem: grupo.ordem,
        obra_id: obraId,
        created_by: userId || null,
        created_at: timestamp,
        updated_at: timestamp
      }));

      const { data: gruposCriados, error: errorCriar } = await supabase
        .from('grupos_tarefas')
        .insert(gruposParaCriar)
        .select('*');

      if (errorCriar) throw errorCriar;

      console.log(`✅ ${gruposCriados?.length || 0} grupos padrão criados para obra ${obraId}`);
      return gruposCriados || [];

    } else {
      // Offline: buscar do cache
      console.log('📱 criarGruposPadraoObra: Modo offline, buscando do cache...');
      const cs = await getCacheService();
      const gruposCache = await cs.getCachedGruposTarefasByObra(obraId);
      
      if (gruposCache.length === 0) {
        // Criar grupos temporários
        const timestamp = new Date().toISOString();
        const gruposTemp = GRUPOS_PADRAO.map(grupo => ({
          id: `temp_grupo_${obraId}_${grupo.ordem}`,
          nome: grupo.nome,
          cor: grupo.cor,
          ordem: grupo.ordem,
          obra_id: obraId,
          created_at: timestamp,
          updated_at: timestamp
        }));

        // Adicionar à fila de sincronização
        for (const grupo of GRUPOS_PADRAO) {
          await offlineSyncService.addToQueue({
            type: 'CREATE',
            table: 'grupos_tarefas',
            data: {
              nome: grupo.nome,
              cor: grupo.cor,
              ordem: grupo.ordem,
              obra_id: obraId,
              created_by: userId
            }
          });
        }

        await cs.cacheGruposTarefas(gruposTemp);
        return gruposTemp.map(grupo => ({
          ...grupo,
          created_by: userId || null,
          updated_by: userId || null
        }));
      }

      return gruposCache;
    }
  } catch (error) {
    console.error('❌ Erro ao criar grupos padrão:', error);
    throw error;
  }
}

// ✅ NOVA FUNÇÃO: Determinar grupo correto baseado na data e status
export function determinarGrupoAutomatico(tarefa: { 
  data_inicio?: string | null; 
  data_limite?: string | null; 
  status?: string;
}, grupos: GrupoTarefa[]): string | null {
  try {
    console.log(`🔄 determinarGrupoAutomatico: Iniciando determinação para tarefa:`, {
      status: tarefa.status,
      data_inicio: tarefa.data_inicio,
      data_limite: tarefa.data_limite,
      gruposCount: grupos.length
    });
  
    // Se está concluída, vai para "Concluídas"
    if (tarefa.status === 'concluido') {
      const grupoConcluidas = grupos.find(g => g.nome === 'Concluídas');
      console.log(`✅ determinarGrupoAutomatico: Tarefa concluída → grupo "Concluídas": ${grupoConcluidas?.id || 'não encontrado'}`);
      return grupoConcluidas?.id || null;
    }

    const hoje = new Date();
    const inicioSemana = new Date(hoje);
    inicioSemana.setDate(hoje.getDate() - hoje.getDay()); // Domingo
    inicioSemana.setHours(0, 0, 0, 0);
    
    const fimSemana = new Date(inicioSemana);
    fimSemana.setDate(inicioSemana.getDate() + 6); // Sábado
    fimSemana.setHours(23, 59, 59, 999);

    console.log(`📅 determinarGrupoAutomatico: Período da semana: ${inicioSemana.toISOString()} até ${fimSemana.toISOString()}`);

    // ✅ REGRA: Tarefas atrasadas SEMPRE vão para "Atividades da Semana"
    if (tarefa.data_limite && isTaskOverdue(tarefa.data_limite, tarefa.status || 'nao_iniciado')) {
      const grupoSemana = grupos.find(g => g.nome === 'Atividades da Semana');
      console.log(`⚠️ determinarGrupoAutomatico: Tarefa atrasada → grupo "Atividades da Semana": ${grupoSemana?.id || 'não encontrado'}`);
      return grupoSemana?.id || null;
    }

    // Se tem data_inicio, usar ela para determinar o grupo
    if (tarefa.data_inicio) {
      const dataInicio = new Date(tarefa.data_inicio);
      
      // Se é desta semana, vai para "Atividades da Semana"
      if (dataInicio >= inicioSemana && dataInicio <= fimSemana) {
        const grupoSemana = grupos.find(g => g.nome === 'Atividades da Semana');
        console.log(`📅 determinarGrupoAutomatico: Tarefa desta semana → grupo "Atividades da Semana": ${grupoSemana?.id || 'não encontrado'}`);
        return grupoSemana?.id || null;
      }
      
      // Se é futura, vai para "Atividades Futuras"
      if (dataInicio > fimSemana) {
        const grupoFuturas = grupos.find(g => g.nome === 'Atividades Futuras');
        console.log(`📅 determinarGrupoAutomatico: Tarefa futura → grupo "Atividades Futuras": ${grupoFuturas?.id || 'não encontrado'}`);
        return grupoFuturas?.id || null;
      }
    }

    // ✅ FALLBACK: Sem data ou data muito antiga → "Atividades da Semana"
    const grupoSemana = grupos.find(g => g.nome === 'Atividades da Semana');
    console.log(`🔄 determinarGrupoAutomatico: Fallback → grupo "Atividades da Semana": ${grupoSemana?.id || 'não encontrado'}`);
    return grupoSemana?.id || null;

  } catch (error) {
    console.error('❌ determinarGrupoAutomatico: Erro ao determinar grupo:', error);
    console.error('❌ determinarGrupoAutomatico: Detalhes do erro:', {
      message: error instanceof Error ? error.message : 'Erro desconhecido',
      stack: error instanceof Error ? error.stack : undefined,
      tarefa,
      gruposCount: grupos.length
    });
    
    // Fallback seguro: retornar null em caso de erro
    return null;
  }
}



// ✅ FUNÇÃO: Buscar responsáveis em batch otimizado
async function buscarResponsaveisEmBatchOtimizado(tarefaIds: string[]): Promise<any[]> {
  if (tarefaIds.length === 0) return [];

  try {
    console.log(`👥 buscarResponsaveisEmBatchOtimizado: Buscando responsáveis para ${tarefaIds.length} tarefas`);
    console.log(`👥 buscarResponsaveisEmBatchOtimizado: IDs das tarefas:`, tarefaIds);

    if (isOnline()) {
      console.log(`👥 buscarResponsaveisEmBatchOtimizado: Modo online - executando query...`);
      // ✅ OTIMIZAÇÃO CRÍTICA: Uma query só para TODAS as tarefas (em vez de loop)
      const { data: responsaveis, error } = await supabase
        .from('tarefas_responsaveis')
        .select(`
          id,
          tarefa_id,
          usuario_id,
          created_at,
          created_by,
          responsavel_usuario:usuarios!fk_tarefas_responsaveis_usuario (id, nome, email)
        `)
        .in('tarefa_id', tarefaIds);

      if (error) {
        console.error('❌ buscarResponsaveisEmBatchOtimizado: Erro na query:', error);
        console.error('❌ buscarResponsaveisEmBatchOtimizado: Detalhes do erro:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        return [];
      }

      console.log(`👥 buscarResponsaveisEmBatchOtimizado: Query executada com sucesso, ${responsaveis?.length || 0} responsáveis encontrados`);

      // ✅ Cache em batch (não individual)
      if (responsaveis && responsaveis.length > 0) {
        console.log(`👥 buscarResponsaveisEmBatchOtimizado: Cacheando responsáveis em background...`);
        // Cache em background (não bloqueia)
        getCacheService().then(cs => cs.cacheTarefasResponsaveis(responsaveis)).catch(error => 
          console.warn('⚠️ buscarResponsaveisEmBatchOtimizado: Erro ao cachear responsáveis (não crítico):', error)
        );
      }

      console.log(`✅ buscarResponsaveisEmBatchOtimizado: ${responsaveis?.length || 0} responsáveis carregados`);
      return responsaveis || [];

    } else {
      // ✅ Offline: Buscar do cache em batch paralelo
      console.log('📱 buscarResponsaveisEmBatchOtimizado: Modo offline');
      
      // ✅ PARALELIZAR: Buscas do cache
      const cs = await getCacheService();
      const buscarPromises = tarefaIds.map(async (tarefaId) => {
        try {
          console.log(`📱 buscarResponsaveisEmBatchOtimizado: Buscando responsáveis da tarefa ${tarefaId} do cache...`);
          const responsaveis = await cs.getCachedTarefasResponsaveisByTarefa(tarefaId);
          console.log(`📱 buscarResponsaveisEmBatchOtimizado: ${responsaveis.length} responsáveis encontrados para tarefa ${tarefaId}`);
          return responsaveis;
        } catch (error) {
          console.warn(`⚠️ buscarResponsaveisEmBatchOtimizado: Erro ao buscar responsáveis da tarefa ${tarefaId} do cache:`, error);
          return [];
        }
      });

      console.log(`📱 buscarResponsaveisEmBatchOtimizado: Aguardando todas as buscas do cache...`);
      const resultados = await Promise.all(buscarPromises);
      const responsaveisCache = resultados.flat();

      console.log(`📱 buscarResponsaveisEmBatchOtimizado: ${responsaveisCache.length} responsáveis carregados do cache`);
      return responsaveisCache;
    }
  } catch (error) {
    console.error('❌ buscarResponsaveisEmBatchOtimizado: Erro geral:', error);
    console.error('❌ buscarResponsaveisEmBatchOtimizado: Detalhes do erro:', {
      message: error instanceof Error ? error.message : 'Erro desconhecido',
      stack: error instanceof Error ? error.stack : undefined,
      tarefaIds,
      isOnline: isOnline()
    });
    return [];
  }
}

// ✅ FUNÇÃO: Sanitizar dados para inserção de tarefa
function sanitizeInsertTask(p: any) {
  const out: any = {};
  // apenas colunas permitidas para INSERT
  const allow = [
    'grupo_id','obra_id','nome','descricao','responsavel_id','status','prioridade',
    'data_limite','data_inicio','data_inicio_tem_hora','subetapa_id','multiplos_responsaveis','progresso_pct','etapa'
  ];
  for (const k of allow) if (k in p) out[k] = p[k];
  // requisitos / coerências
  if (!isUUID(out.grupo_id)) throw new Error('grupo_id inválido');
  if (!isUUID(out.obra_id)) throw new Error('obra_id inválido');
  if (out.responsavel_id && !isUUID(out.responsavel_id)) out.responsavel_id = null;
  if (out.subetapa_id && !isUUID(out.subetapa_id)) out.subetapa_id = null;
  // nunca enviar id no insert (DB gera)
  delete out.id;
  // saneamento de lixo de UI
  delete out._ui;
  delete out._obra;
  delete out.grupo;
  return out;
}



// ✅ FUNÇÃO: Verificar se tarefa está atrasada
function isTaskOverdue(dataLimite: string | null, status: string): boolean {
  if (!dataLimite || status === 'concluido') return false;
  const hoje = new Date();
  const limite = new Date(dataLimite);
  return hoje > limite;
}

// ✅ FUNÇÃO: Buscar tarefa por ID com responsáveis
export async function getTarefaById(tarefaId: string): Promise<any> {
  console.log(`🔍 getTarefaById: Buscando tarefa ${tarefaId}`);
  
  try {
    console.log(`🔍 getTarefaById: Executando query no Supabase...`);
    const { data, error } = await supabase
      .from('tarefas')
      .select(`
        *,
        grupos_tarefas (id, nome, cor, ordem, obra_id, created_at, updated_at),
        created_by_user:usuarios!tarefas_created_by_fkey (id, nome, email),
        updated_by_user:usuarios!tarefas_updated_by_fkey (id, nome, email)
      `)
      .eq('id', tarefaId)
      .maybeSingle();

    if (error) {
      console.error(`❌ getTarefaById: Erro na query - ${error.message}`);
      console.error(`❌ getTarefaById: Detalhes do erro:`, {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
        tarefaId
      });
      throw new Error(`Erro ao buscar tarefa: ${(error as any).message}`);
    }

    if (!data) {
      console.error(`❌ getTarefaById: Tarefa ${tarefaId} não encontrada`);
      throw new Error(`Tarefa com ID ${tarefaId} não encontrada`);
    }

    console.log(`✅ getTarefaById: Tarefa ${tarefaId} encontrada - nome: ${data.nome}`);

    // ✅ OTIMIZAÇÃO: Buscar responsáveis em batch (mesmo que seja só 1 tarefa)
    console.log(`🔍 getTarefaById: Buscando responsáveis em batch...`);
    const responsaveisMultiplos = await buscarResponsaveisEmBatchOtimizado([tarefaId]);
    console.log(`✅ getTarefaById: ${responsaveisMultiplos.length} responsáveis encontrados`);

    const tarefaProcessada = {
      ...data,
      grupos_tarefas: data.grupos_tarefas,
      created_by_tarefas_created_by_fkey: data.created_by_user,
      updated_by_tarefas_updated_by_fkey: data.updated_by_user,
      // ✅ PADRÃO: Estrutura consistente para responsáveis
      tarefas_responsaveis: (responsaveisMultiplos || []).map((r: any) => ({
        id: r.id,
        usuario_id: r.usuario_id,
        usuario: r.responsavel_usuario
      })),
      responsaveis: (responsaveisMultiplos || []).map((r: any) => r.responsavel_usuario).filter(Boolean),
      responsaveis_ids: (responsaveisMultiplos || []).map((r: any) => r.usuario_id),
      // ✅ COMPATIBILIDADE: Manter responsavel_id para compatibilidade
      responsavel_id: (responsaveisMultiplos || []).length === 1 ? (responsaveisMultiplos || [])[0].usuario_id : null
    };

    console.log(`✅ getTarefaById: Tarefa ${tarefaId} processada com sucesso`);
    return tarefaProcessada;

  } catch (error) {
    console.error(`❌ getTarefaById: Erro geral:`, error);
    console.error(`❌ getTarefaById: Detalhes do erro:`, {
      message: error instanceof Error ? error.message : 'Erro desconhecido',
      stack: error instanceof Error ? error.stack : undefined,
      tarefaId
    });
    throw error;
  }
}

// ✅ FUNÇÃO: Buscar responsáveis de uma tarefa
export async function getTarefaResponsaveis(tarefaId: string): Promise<Array<{id: string, nome: string, email: string}>> {
  try {
    console.log(`🔍 getTarefaResponsaveis: Buscando responsáveis para tarefa ${tarefaId}`);
    
    const { data, error } = await supabase
      .from('tarefas_responsaveis')
      .select(`
        id,
        usuario_id,
        responsavel_usuario:usuarios!fk_tarefas_responsaveis_usuario (id, nome, email)
      `)
      .eq('tarefa_id', tarefaId);

    if (error) {
      throw new Error(`Erro ao buscar responsáveis: ${error.message}`);
    }

    // ✅ CORREÇÃO: Retornar estrutura correta com id do relacionamento
    const responsaveis = (data || []).map((tr: any) => ({
      id: tr.id, // ID do relacionamento tarefas_responsaveis
      nome: tr.responsavel_usuario?.nome || '',
      email: tr.responsavel_usuario?.email || ''
    })).filter(r => r.nome && r.email);

    console.log(`✅ getTarefaResponsaveis: ${responsaveis.length} responsáveis encontrados para tarefa ${tarefaId}`);
    return responsaveis;
  } catch (error) {
    console.error('❌ Erro ao buscar responsáveis da tarefa:', error);
    return [];
  }
}

// ✅ FUNÇÃO: Atualizar responsáveis de uma tarefa
export async function updateTarefaResponsaveis(
  tarefaId: string, 
  responsaveisIds: string[], 
  userId?: string
): Promise<void> {
  try {
    console.log(`🔄 updateTarefaResponsaveis: Atualizando responsáveis para tarefa ${tarefaId}`);
    
    // ✅ CORREÇÃO: Remover responsáveis existentes
    await supabase
      .from('tarefas_responsaveis')
      .delete()
      .eq('tarefa_id', tarefaId);

    // ✅ CORREÇÃO: Adicionar novos responsáveis
    if (responsaveisIds.length > 0) {
      const responsaveisData = responsaveisIds.map(usuarioId => ({
        tarefa_id: tarefaId,
        usuario_id: usuarioId,
        created_by: userId || null,
        created_at: new Date().toISOString()
      }));

      const { error } = await supabase
        .from('tarefas_responsaveis')
        .insert(responsaveisData);

      if (error) {
        throw new Error(`Erro ao atualizar responsáveis: ${error.message}`);
      }
    }

    // ✅ CORREÇÃO: Atualizar apenas campo multiplos_responsaveis (não responsavel_id obsoleto)
    await supabase
      .from('tarefas')
      .update({ 
        multiplos_responsaveis: responsaveisIds.length > 1,
        updated_at: new Date().toISOString()
      })
      .eq('id', tarefaId);
      
    console.log(`✅ updateTarefaResponsaveis: Responsáveis atualizados com sucesso para tarefa ${tarefaId}`);
  } catch (error) {
    console.error('❌ Erro ao atualizar responsáveis da tarefa:', error);
    throw error;
  }
}

// ✅ FUNÇÃO: Buscar usuários por obra (excluindo clientes)
export async function getUsuariosByObra(): Promise<any[]> {
  const { data, error } = await supabase
    .from('usuarios')
    .select('id, nome, email, role, funcao')
    .eq('status', 'ativo')
    .neq('role', 'cliente') // ✅ Excluir usuários com role cliente
    .neq('funcao', 'cliente') // ✅ Excluir usuários com funcao cliente
    .order('nome', { ascending: true });

  if (error) {
    console.error('❌ getUsuariosByObra: Erro ao buscar usuários:', error);
    throw new Error(`Erro ao buscar usuários: ${error.message}`);
  }

  return data || [];
}

// ✅ FUNÇÃO: Buscar grupo por ID
export async function getGrupoById(grupoId: string): Promise<GrupoTarefa | null> {
  const { data, error } = await supabase
    .from('grupos_tarefas')
    .select('*')
    .eq('id', grupoId)
    .single();

  if (error) {
    return null;
  }

  return data;
}

export async function convertTasksToServicos(params: {
  tarefaIds: string[];
  empresaId?: string;
  empresaNome?: string;
  statusServico?: 'nao_iniciado'|'agendado'|'em_andamento'|'parado'|'concluido';
  concluirTarefas?: boolean;
}) {
  const { tarefaIds, empresaId, empresaNome, statusServico = 'nao_iniciado', concluirTarefas = true } = params;
  if (!tarefaIds?.length) return [];
  console.debug('[convertTasksToServicos] params', { tarefaIds, empresaId, empresaNome, statusServico, concluirTarefas });
  const { data, error } = await supabase.rpc('tarefas_convert_to_servicos', {
    p_tarefa_ids: tarefaIds,
    p_empresa_id: empresaId ?? null,
    p_empresa_nome: empresaNome ?? null,
    p_concluir_tarefas: concluirTarefas,
    p_status_servico: statusServico
  });
  if (error) throw error;
  console.debug('[convertTasksToServicos] data', data);
  return data || [];
}
